import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { WelcomeMessageStylePicker } from './WelcomeMessageStylePicker';
import { WelcomeMessagePreview } from './WelcomeMessagePreview';
import type { WelcomeMessage } from '@/hooks/useWelcomeMessages';
import type { CreateWelcomeMessageInput } from '@/hooks/useAdminWelcomeMessages';

const formSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be 100 characters or less'),
  body: z.string().min(1, 'Body is required'),
  target_audience: z.enum(['vendors', 'clients', 'both']),
  style: z.enum(['info', 'success', 'warning', 'important']),
  start_date: z.date(),
  end_date: z.date().nullable(),
  has_end_date: z.boolean(),
  display_mode: z.enum(['always', 'dismissable']),
  priority: z.number().min(0).max(100),
  status: z.enum(['draft', 'active', 'archived']),
});

type FormValues = z.infer<typeof formSchema>;

interface WelcomeMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message?: WelcomeMessage | null;
  onSave: (data: CreateWelcomeMessageInput) => Promise<void>;
  isSaving: boolean;
}

export function WelcomeMessageDialog({
  open,
  onOpenChange,
  message,
  onSave,
  isSaving,
}: WelcomeMessageDialogProps) {
  const isEditing = !!message;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      body: '',
      target_audience: 'both',
      style: 'info',
      start_date: new Date(),
      end_date: null,
      has_end_date: false,
      display_mode: 'dismissable',
      priority: 0,
      status: 'draft',
    },
  });

  // Reset form when message changes
  useEffect(() => {
    if (message) {
      form.reset({
        title: message.title,
        body: message.body,
        target_audience: message.target_audience,
        style: message.style,
        start_date: new Date(message.start_date),
        end_date: message.end_date ? new Date(message.end_date) : null,
        has_end_date: !!message.end_date,
        display_mode: message.display_mode,
        priority: message.priority,
        status: message.status,
      });
    } else {
      form.reset({
        title: '',
        body: '',
        target_audience: 'both',
        style: 'info',
        start_date: new Date(),
        end_date: null,
        has_end_date: false,
        display_mode: 'dismissable',
        priority: 0,
        status: 'draft',
      });
    }
  }, [message, form]);

  const watchedValues = form.watch();

  const onSubmit = async (values: FormValues) => {
    const data: CreateWelcomeMessageInput = {
      title: values.title,
      body: values.body,
      target_audience: values.target_audience,
      style: values.style,
      start_date: values.start_date.toISOString(),
      end_date: values.has_end_date && values.end_date ? values.end_date.toISOString() : null,
      display_mode: values.display_mode,
      priority: values.priority,
      status: values.status,
    };

    await onSave(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Welcome Message' : 'Create Welcome Message'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          {/* Form Column */}
          <div className="space-y-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Welcome to our platform!"
                            maxLength={100}
                            {...field}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            {field.value.length}/100
                          </span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="body"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Body (Markdown supported)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Write your message here. **Bold** and *italic* text, [links](url), and lists are supported."
                          className="min-h-[120px] font-mono text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Supports **bold**, *italic*, [links](url), and lists
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="target_audience"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Audience</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select audience" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="both">All Users</SelectItem>
                          <SelectItem value="clients">Clients Only</SelectItem>
                          <SelectItem value="vendors">Vendors Only</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="style"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Style</FormLabel>
                      <FormControl>
                        <WelcomeMessageStylePicker
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Start Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="end_date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>End Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground'
                                )}
                                disabled={!watchedValues.has_end_date}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>No end date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value || undefined}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date < (watchedValues.start_date || new Date())
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="has_end_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Set End Date</FormLabel>
                        <FormDescription>
                          Enable to automatically stop showing the message after a date
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="display_mode"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Allow Dismissal</FormLabel>
                        <FormDescription>
                          {field.value === 'dismissable'
                            ? 'Users can dismiss this message permanently'
                            : 'Message will show every session until end date'}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value === 'dismissable'}
                          onCheckedChange={(checked) =>
                            field.onChange(checked ? 'dismissable' : 'always')
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority (0-100)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormDescription>
                        Higher priority messages are shown first
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? 'Saving...' : isEditing ? 'Update Message' : 'Create Message'}
                  </Button>
                </div>
              </form>
            </Form>
          </div>

          {/* Preview Column */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Live Preview</h3>
            <WelcomeMessagePreview
              title={watchedValues.title}
              body={watchedValues.body}
              style={watchedValues.style}
              displayMode={watchedValues.display_mode}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
